import { brUfs } from '@/shared/constants/brUfs';
import { isValidEmail, isValidTelefone } from '@/shared/utils/contato';
import { applyCpfCnpjMask } from '@/shared/utils/cnpj';
import { isValidCpfCnpj, normalizeCpfCnpj } from '@/shared/utils/documentValidation';

import type {
  AdicionarAdvogadoParteInput,
  AdvogadoDaParteItem,
  CriarParteArbitralInput,
  EditarAdvogadoParteInput,
  EditarParteArbitralInput,
  ParteCompletaItem,
  ParteEmailInput,
  ParteTelefoneInput,
  PapelParteEditavel,
  TipoPessoaParteInput,
  UfOab,
  VinculosEscritaInput,
} from '@/features/secretaria-arbitral/types';

export const JUSTIFICATIVA_MIN = 10;
export const JUSTIFICATIVA_MAX = 1000;
export const OAB_NUMERO_MAX = 20;

const CONTATOS_MAX = 5;
const NOME_PARTE_MIN = 3;
const OAB_NUMERO_MIN = 2;

export const PAPEIS_PARTE_EDITAVEIS = [
  'REQUERENTE',
  'REQUERIDO',
] as const satisfies readonly PapelParteEditavel[];

export const PAPEL_PARTE_LABEL: Record<PapelParteEditavel, string> = {
  REQUERENTE: 'Requerente',
  REQUERIDO: 'Requerido',
};

export const TIPOS_PESSOA_PARTE = [
  'FISICA',
  'JURIDICA',
] as const satisfies readonly TipoPessoaParteInput[];

export const TIPO_PESSOA_PARTE_LABEL: Record<TipoPessoaParteInput, string> = {
  FISICA: 'Pessoa física',
  JURIDICA: 'Pessoa jurídica',
};

export interface ParteFormValores {
  papel: PapelParteEditavel;
  tipoPessoa: TipoPessoaParteInput;
  documento: string;
  nome: string;
  emails: string[];
  telefones: string[];
  justificativa: string;
}

export interface AdvogadoFormValores {
  nome: string;
  oabNumero: string;
  oabUf: string;
  cpfCnpj: string;
  email: string;
  telefone: string;
  justificativa: string;
}

export const PARTE_FORM_VAZIA: ParteFormValores = {
  papel: 'REQUERENTE',
  tipoPessoa: 'FISICA',
  documento: '',
  nome: '',
  emails: [''],
  telefones: [''],
  justificativa: '',
};

export const ADVOGADO_FORM_VAZIO: AdvogadoFormValores = {
  nome: '',
  oabNumero: '',
  oabUf: 'SP',
  cpfCnpj: '',
  email: '',
  telefone: '',
  justificativa: '',
};

export function justificativaValida(
  texto: string,
  minimo: number = JUSTIFICATIVA_MIN,
  maximo: number = JUSTIFICATIVA_MAX,
): boolean {
  const tamanho = texto.trim().length;
  return tamanho >= minimo && tamanho <= maximo;
}

export function nomeParteValido(nome: string): boolean {
  return nome.trim().length >= NOME_PARTE_MIN;
}

export function ehPapelParteEditavel(
  valor: string | null | undefined,
): valor is PapelParteEditavel {
  return PAPEIS_PARTE_EDITAVEIS.some((papel) => papel === valor);
}

export function ehTipoPessoaParte(valor: string | null | undefined): valor is TipoPessoaParteInput {
  return TIPOS_PESSOA_PARTE.some((tipo) => tipo === valor);
}

export function ehUfOab(valor: string | null | undefined): valor is UfOab {
  return brUfs.some((uf) => uf === valor);
}

export function rotuloDocumentoParte(tipoPessoa: TipoPessoaParteInput): string {
  return tipoPessoa === 'JURIDICA' ? 'CNPJ' : 'CPF';
}

export function placeholderDocumentoParte(tipoPessoa: TipoPessoaParteInput): string {
  return tipoPessoa === 'JURIDICA' ? '00.000.000/0000-00' : '000.000.000-00';
}

export function rotuloNomeParte(tipoPessoa: TipoPessoaParteInput): string {
  return tipoPessoa === 'JURIDICA' ? 'Razão social' : 'Nome completo';
}

export function normalizarOabNumero(valor: string): string {
  return valor.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
}

export function oabNumeroValido(valor: string): boolean {
  const normalizado = normalizarOabNumero(valor);
  return normalizado.length >= OAB_NUMERO_MIN && normalizado.length <= OAB_NUMERO_MAX;
}

function limparContatos(valores: string[]): string[] {
  return valores
    .map((valor) => valor.trim())
    .filter((valor) => valor.length > 0)
    .slice(0, CONTATOS_MAX);
}

function emailsInvalidos(valores: string[]): boolean {
  return limparContatos(valores).some((email) => !isValidEmail(email));
}

function telefonesInvalidos(valores: string[]): boolean {
  return limparContatos(valores).some((telefone) => !isValidTelefone(telefone));
}

export function errosDeEmails(valores: string[]): Array<string | undefined> {
  return valores.map((email) =>
    email.trim().length > 0 && !isValidEmail(email.trim()) ? 'E-mail inválido' : undefined,
  );
}

export function errosDeTelefones(valores: string[]): Array<string | undefined> {
  return valores.map((telefone) =>
    telefone.trim().length > 0 && !isValidTelefone(telefone) ? 'Telefone inválido' : undefined,
  );
}

function paraEmailsInput(valores: string[]): ParteEmailInput[] {
  return limparContatos(valores).map((email, indice) => ({
    email: email.toLowerCase(),
    principal: indice === 0,
  }));
}

function paraTelefonesInput(valores: string[]): ParteTelefoneInput[] {
  return limparContatos(valores).map((numero, indice) => ({
    numero,
    principal: indice === 0,
  }));
}

function mesmaListaDeContatos(anteriores: string[], atuais: string[]): boolean {
  const antes = limparContatos(anteriores);
  const depois = limparContatos(atuais);
  return antes.length === depois.length && antes.every((valor, i) => valor === depois[i]);
}

export function documentoParteValido(documento: string): boolean {
  return isValidCpfCnpj(documento);
}

export function podeSalvarNovaParte(valores: ParteFormValores): boolean {
  return (
    documentoParteValido(valores.documento) &&
    nomeParteValido(valores.nome) &&
    justificativaValida(valores.justificativa) &&
    !emailsInvalidos(valores.emails) &&
    !telefonesInvalidos(valores.telefones)
  );
}

export function montarCriarPartePayload(
  valores: ParteFormValores,
  vinculos: VinculosEscritaInput = {},
): CriarParteArbitralInput {
  const emails = paraEmailsInput(valores.emails);
  const telefones = paraTelefonesInput(valores.telefones);
  return {
    cpfCnpj: normalizeCpfCnpj(valores.documento),
    nome: valores.nome.trim(),
    tipoPessoa: valores.tipoPessoa,
    papel: valores.papel,
    justificativa: valores.justificativa.trim(),
    ...(emails.length > 0 ? { emails } : {}),
    ...(telefones.length > 0 ? { telefones } : {}),
    ...vinculos,
  };
}

export function montarEditarPartePayload(
  iniciais: ParteFormValores,
  valores: ParteFormValores,
  vinculos: VinculosEscritaInput = {},
): EditarParteArbitralInput {
  const payload: EditarParteArbitralInput = {
    justificativa: valores.justificativa.trim(),
    ...vinculos,
  };
  if (valores.papel !== iniciais.papel) payload.papel = valores.papel;
  if (valores.nome.trim() !== iniciais.nome.trim()) payload.nome = valores.nome.trim();
  if (!mesmaListaDeContatos(iniciais.emails, valores.emails)) {
    payload.emails = paraEmailsInput(valores.emails);
  }
  if (!mesmaListaDeContatos(iniciais.telefones, valores.telefones)) {
    payload.telefones = paraTelefonesInput(valores.telefones);
  }
  return payload;
}

export function temAlteracaoDeParte(
  iniciais: ParteFormValores,
  valores: ParteFormValores,
  vinculos: VinculosEscritaInput = {},
): boolean {
  const { justificativa: _justificativa, ...alteracoes } = montarEditarPartePayload(
    iniciais,
    valores,
    vinculos,
  );
  return Object.keys(alteracoes).length > 0;
}

export function podeSalvarEdicaoDeParte(
  iniciais: ParteFormValores,
  valores: ParteFormValores,
  vinculos: VinculosEscritaInput = {},
): boolean {
  return (
    nomeParteValido(valores.nome) &&
    justificativaValida(valores.justificativa) &&
    !emailsInvalidos(valores.emails) &&
    !telefonesInvalidos(valores.telefones) &&
    temAlteracaoDeParte(iniciais, valores, vinculos)
  );
}

export function valoresDaParte(parte: ParteCompletaItem): ParteFormValores {
  const papelVigente = parte.papeisVigentes.find((papel) => ehPapelParteEditavel(papel));
  const emails = parte.emails.map((item) => item.email);
  const telefones = parte.telefones.map((item) => item.numeroFormatado ?? item.numeroNormalizado);
  return {
    papel: ehPapelParteEditavel(papelVigente) ? papelVigente : 'REQUERENTE',
    tipoPessoa: parte.pessoa.tipoPessoa === 'JURIDICA' ? 'JURIDICA' : 'FISICA',
    documento: applyCpfCnpjMask(parte.pessoa.cpfCnpjFormatado ?? parte.pessoa.cpfCnpj),
    nome: parte.pessoa.nomeCompleto,
    emails: emails.length > 0 ? emails : [''],
    telefones: telefones.length > 0 ? telefones : [''],
    justificativa: '',
  };
}

export function podeSalvarAdvogado(valores: AdvogadoFormValores): boolean {
  const documentoOk = valores.cpfCnpj.trim().length === 0 || isValidCpfCnpj(valores.cpfCnpj);
  const emailOk = valores.email.trim().length === 0 || isValidEmail(valores.email);
  const telefoneOk = valores.telefone.trim().length === 0 || isValidTelefone(valores.telefone);
  return (
    nomeParteValido(valores.nome) &&
    oabNumeroValido(valores.oabNumero) &&
    ehUfOab(valores.oabUf) &&
    justificativaValida(valores.justificativa) &&
    documentoOk &&
    emailOk &&
    telefoneOk
  );
}

export function montarAdicionarAdvogadoPayload(
  valores: AdvogadoFormValores,
  vinculos: VinculosEscritaInput = {},
): AdicionarAdvogadoParteInput {
  const documento = normalizeCpfCnpj(valores.cpfCnpj);
  const email = valores.email.trim().toLowerCase();
  const telefone = valores.telefone.trim();
  return {
    nome: valores.nome.trim(),
    oabNumero: normalizarOabNumero(valores.oabNumero),
    oabUf: ehUfOab(valores.oabUf) ? valores.oabUf : 'SP',
    justificativa: valores.justificativa.trim(),
    ...(documento.length > 0 ? { cpfCnpj: documento } : {}),
    ...(email.length > 0 ? { email } : {}),
    ...(telefone.length > 0 ? { telefone } : {}),
    ...vinculos,
  };
}

export function montarEditarAdvogadoPayload(
  iniciais: AdvogadoFormValores,
  valores: AdvogadoFormValores,
  vinculos: VinculosEscritaInput = {},
): EditarAdvogadoParteInput {
  const payload: EditarAdvogadoParteInput = {
    justificativa: valores.justificativa.trim(),
    ...vinculos,
  };
  if (valores.nome.trim() !== iniciais.nome.trim()) payload.nome = valores.nome.trim();
  if (normalizarOabNumero(valores.oabNumero) !== normalizarOabNumero(iniciais.oabNumero)) {
    payload.oabNumero = normalizarOabNumero(valores.oabNumero);
  }
  if (valores.oabUf !== iniciais.oabUf && ehUfOab(valores.oabUf)) payload.oabUf = valores.oabUf;
  const email = valores.email.trim().toLowerCase();
  if (email !== iniciais.email.trim().toLowerCase()) {
    payload.email = email.length > 0 ? email : null;
  }
  const telefone = valores.telefone.trim();
  if (telefone !== iniciais.telefone.trim()) {
    payload.telefone = telefone.length > 0 ? telefone : null;
  }
  return payload;
}

export function temAlteracaoDeAdvogado(
  iniciais: AdvogadoFormValores,
  valores: AdvogadoFormValores,
  vinculos: VinculosEscritaInput = {},
): boolean {
  const { justificativa: _justificativa, ...alteracoes } = montarEditarAdvogadoPayload(
    iniciais,
    valores,
    vinculos,
  );
  return Object.keys(alteracoes).length > 0;
}

export function podeSalvarEdicaoDeAdvogado(
  iniciais: AdvogadoFormValores,
  valores: AdvogadoFormValores,
  vinculos: VinculosEscritaInput = {},
): boolean {
  return podeSalvarAdvogado(valores) && temAlteracaoDeAdvogado(iniciais, valores, vinculos);
}

export function valoresDoAdvogado(advogado: AdvogadoDaParteItem): AdvogadoFormValores {
  return {
    nome: advogado.nome,
    oabNumero: advogado.oabNumero ?? '',
    oabUf: ehUfOab(advogado.oabUf) ? advogado.oabUf : 'SP',
    cpfCnpj: advogado.cpfCnpjFormatado ?? '',
    email: advogado.emails[0] ?? '',
    telefone: advogado.telefones[0] ?? '',
    justificativa: '',
  };
}

export function descreverOab(advogado: AdvogadoDaParteItem): string {
  if (!advogado.oabNumero || !advogado.oabUf) return 'OAB não informada';
  return `OAB ${advogado.oabUf} ${advogado.oabNumero}`;
}

/**
 * Texto do contador de justificativa.
 *
 * O denominador cru era o MÍNIMO enquanto `maxLength` é o máximo, então um texto
 * válido de 45 caracteres exibia "45/10 caracteres". Enquanto falta atingir o mínimo
 * a mensagem diz quanto falta; depois vira `atual/MAX`, que é o limite real.
 */
export function textoContadorJustificativa(
  atual: number,
  minimo: number = JUSTIFICATIVA_MIN,
  maximo: number = JUSTIFICATIVA_MAX,
): string {
  if (atual < minimo) return `Mínimo de ${minimo} caracteres`;
  return `${atual}/${maximo} caracteres`;
}
